<template>
  <v-tooltip bottom>
    <template v-slot:activator="{ on, attrs }">
      <v-btn v-if="!props.size" icon @click.stop="printTicket" :loading="downloading" :disabled="downloading" v-bind="attrs" v-on="on">
        <v-icon>mdi-printer</v-icon>
      </v-btn>
      <v-btn v-else-if="props.size === 'small'" small icon @click.stop="printTicket" :loading="downloading" :disabled="downloading" v-bind="attrs" v-on="on">
        <v-icon small>mdi-printer</v-icon>
      </v-btn>
      <v-btn v-else-if="props.size === 'x-small'" x-small icon @click.stop="printTicket" :loading="downloading" :disabled="downloading" v-bind="attrs" v-on="on">
        <v-icon small>mdi-printer</v-icon>
      </v-btn>
    </template>
    <span>Print</span>
  </v-tooltip>
</template>

<script setup lang="ts">
import { ref, defineProps, computed } from 'vue';
import { useGetters } from '../store/tickets';
import { getTicketPDF } from '../services/api';

// Props
const props = defineProps<{
  ticketRef?: string;
  size?: string;
}>();

// Local state
const downloading = ref(false);

const printTicket = async () => {
  // Determine which ticket ref to use - lazy evaluation at click time
  let ticketRefToUse: string | undefined;
  
  if (props.ticketRef) {
    ticketRefToUse = props.ticketRef;
  } else {
    // Only get from store when needed (at click)
    const { currentTicket } = useGetters(['currentTicket']);
    ticketRefToUse = currentTicket.value?.ticket_ref;
  }

  if (!ticketRefToUse) {
    console.error('No ticket reference available - neither from prop nor store');
    return;
  }

  try {
    downloading.value = true;

    // Call the API to get the PDF report
    const response = await getTicketPDF(ticketRefToUse);

    // The API returns base64 encoded PDF in the body
    const base64Data = response.body || response;

    // Convert base64 to blob
    const binaryString = atob(base64Data);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    const blob = new Blob([bytes], { type: 'application/pdf' });

    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `ticket-${ticketRefToUse}-report.pdf`;

    // Trigger download
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

  } catch (error) {
    console.error('Error downloading ticket report:', error);
    // You could add a toast notification here if you have a notification system
  } finally {
    downloading.value = false;
  }
};
</script>